import { useEffect } from 'react';

/**
 * Modal de confirmación reutilizable para LMS
 * @param {boolean} open - Si el modal está abierto
 * @param {function} onClose - Callback al cancelar
 * @param {function} onConfirm - Callback al confirmar
 * @param {string|JSX.Element} title - Título del modal
 * @param {string|JSX.Element} message - Mensaje del modal (puede ser HTML/JSX)
 * @param {string} confirmText - Texto del botón de confirmar (default: "Aceptar")
 * @param {string} cancelText - Texto del botón de cancelar (default: "Cancelar")
 * @param {string} variant - Variante visual: 'default', 'danger', 'warning', 'success'
 */
export default function ConfirmDialog({
    open,
    onClose,
    onConfirm,
    title = '¿Estás seguro?',
    message = '',
    confirmText = 'Aceptar',
    cancelText = 'Cancelar',
    variant = 'default'
}) {
    // Cerrar con ESC
    useEffect(() => {
        if (!open) return;
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(500px, 96vw)',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                    padding: '1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}
            >
                {/* Título */}
                <div style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    lineHeight: 1.3
                }}>
                    {title}
                </div>

                {/* Mensaje */}
                <div style={{
                    fontSize: '0.95rem',
                    color: '#475569',
                    lineHeight: 1.5
                }}>
                    {message}
                </div>

                {/* Botones */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.75rem',
                    marginTop: '0.5rem'
                }}>
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={onClose}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className="btn-action"
                        onClick={() => {
                            onConfirm?.();
                            onClose?.();
                        }}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
